import { useState } from "react";
import { VideoPlayer } from "@seeen/player";
import { useApiPlaylist, limitVideos } from "../shared/useApiPlaylist";
import type { VideoItem, CtaOverlay } from "../shared/types";

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0)
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Pre-fetched mode: supply a `videos` array directly. */
type WithVideos = { videos: VideoItem[]; apiKey?: never; playlistId?: never };
/** API-driven mode: supply credentials and the embed fetches the playlist. */
type WithApi = { apiKey: string; playlistId: string; videos?: never };

export type PlaylistEmbedProps = (WithVideos | WithApi) & {
  /** CTA image overlays applied to every video in the playlist. */
  overlays?: CtaOverlay[];
  /** Zero-based index of the video to play first. Defaults to `0`. */
  initialIndex?: number;
  /** Number of videos to fetch per page in API-driven mode. Defaults to 30. Ignored in pre-fetched mode. */
  pageSize?: number;
  /** Maximum number of videos to show. Applies to both modes; in API-driven mode, pagination stops once the limit is reached. Leave unset for no limit. */
  maxVideos?: number;
};

export function PlaylistEmbed({
  overlays = [],
  initialIndex = 0,
  pageSize,
  maxVideos,
  ...props
}: PlaylistEmbedProps) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const apiKey =
    "apiKey" in props && props.apiKey != null ? props.apiKey : null;
  const playlistId =
    "playlistId" in props && props.playlistId != null
      ? props.playlistId
      : null;
  const apiState = useApiPlaylist(apiKey, playlistId, pageSize, maxVideos);

  const videos =
    "videos" in props && props.videos != null
      ? limitVideos(props.videos, maxVideos)
      : apiState.status === "success"
        ? apiState.videos
        : null;

  if (apiState.status === "loading") {
    return <div className="aspect-video w-full bg-black animate-pulse" />;
  }

  if (apiState.status === "error") {
    return (
      <div className="aspect-video w-full bg-black flex items-center justify-center">
        <p className="text-white/50 text-sm">Failed to load playlist.</p>
      </div>
    );
  }

  if (!videos || videos.length === 0) return null;

  const active = videos[activeIndex] ?? videos[0];
  const activeIndexSafe = videos[activeIndex] ? activeIndex : 0;

  return (
    <div className="flex flex-col gap-4">
      <VideoPlayer
        src={active.src}
        poster={active.poster}
        title={active.title}
        thumbnailStoryboardUrl={active.thumbnailStoryboardUrl}
        overlays={overlays}
        sponsor={active.sponsor}
        endCard={active.endCard}
        ctaTimestamps={active.ctaTimestamps}
      />

      <ol className="flex flex-col gap-1">
        {videos.map((video, i) => (
          <li key={video.id}>
            <button
              onClick={() => setActiveIndex(i)}
              className={[
                "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                i === activeIndexSafe
                  ? "bg-slate-900 text-white font-semibold"
                  : "text-slate-700 hover:bg-slate-100",
              ].join(" ")}
            >
              <span className="mr-2 tabular-nums text-xs opacity-50">
                {i + 1}
              </span>
              {video.title}
              {video.duration != null && (
                <span className="float-right text-xs opacity-50">
                  {formatDuration(video.duration)}
                </span>
              )}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
